import type {
  SealedProduct,
  SealedProductFilters,
  SealedProductsFilterOptions,
  SealedSetOption,
  UserFilters,
} from '@/types'
import { getSealedFuseIndex } from './fuseIndex'
import { sortByRelevance } from './sortByRelevance'

function removeHidden(products: SealedProduct[], userFilters: UserFilters): SealedProduct[] {
  const hiddenSets = new Set(userFilters.hiddenSets)
  const hiddenProducts = new Set(userFilters.hiddenSealedProducts)
  return products.filter((p) => !hiddenProducts.has(p.id) && !(p.setCode && hiddenSets.has(p.setCode)))
}

/** Fuzzy-match on name; keeps relevance order when there's no explicit sort. */
function searchByName(all: SealedProduct[], current: SealedProduct[], q: string, hasSort: boolean): SealedProduct[] {
  const searchResults = getSealedFuseIndex(all).search(q)
  const matchIds = new Set(searchResults.map((r) => r.item.id))
  const result = current.filter((p) => matchIds.has(p.id))
  if (!hasSort) {
    const orderMap = new Map(searchResults.map((r, i) => [r.item.id, i]))
    sortByRelevance(result, orderMap)
  }
  return result
}

function filterByOwnership(
  products: SealedProduct[],
  ownership: NonNullable<SealedProductFilters['ownership']>,
  quantities: Record<number, number>,
): SealedProduct[] {
  const wantOwned = ownership === 'owned'
  return products.filter((p) => (quantities[p.id] || 0) > 0 === wantOwned)
}

export function filterSealedProducts(
  products: SealedProduct[],
  filters: SealedProductFilters,
  userFilters?: UserFilters,
  showHidden?: boolean,
  sealedCollectionQuantities?: Record<number, number>,
): SealedProduct[] {
  let result = products

  if (!showHidden && userFilters) result = removeHidden(result, userFilters)
  if (filters.q) result = searchByName(products, result, filters.q, !!filters.sort)
  if (filters.productType) result = result.filter((p) => p.productType === filters.productType)
  if (filters.setCode) result = result.filter((p) => p.setCode === filters.setCode)
  if (filters.ownership && sealedCollectionQuantities) {
    result = filterByOwnership(result, filters.ownership, sealedCollectionQuantities)
  }

  return result
}

const SEALED_COMPARATORS: Record<string, (a: SealedProduct, b: SealedProduct) => number> = {
  name: (a, b) => a.name.localeCompare(b.name),
  product_type: (a, b) => (a.productType ?? '').localeCompare(b.productType ?? ''),
  set_code: (a, b) => (a.setCode ?? '').localeCompare(b.setCode ?? ''),
  price: (a, b) => (a.marketPrice ?? -Infinity) - (b.marketPrice ?? -Infinity),
}

export function sortSealedProducts(products: SealedProduct[], sort?: string, order?: 'asc' | 'desc'): SealedProduct[] {
  if (!sort) return products

  const compare = SEALED_COMPARATORS[sort]
  const sorted = [...products]
  const dir = order === 'desc' ? -1 : 1

  sorted.sort((a, b) => (compare ? compare(a, b) * dir : 0) || a.id - b.id)
  return sorted
}

export function deriveSealedFilterOptions(products: SealedProduct[]): SealedProductsFilterOptions {
  const productTypes = new Set<string>()
  const setsMap = new Map<string, SealedSetOption>()

  for (const product of products) {
    if (product.productType) productTypes.add(product.productType)
    if (product.setCode && !setsMap.has(product.setCode)) {
      setsMap.set(product.setCode, { setCode: product.setCode })
    }
  }

  return {
    productTypes: [...productTypes].sort(),
    sets: [...setsMap.values()].sort((a, b) => a.setCode.localeCompare(b.setCode)),
  }
}
